import { STATUS } from "@/constants/statuses";
import type { CustomerActiveRewardsEntity } from "@apps/rewards/modules/CustomerRewards/models/RewardsCustomerRewardsEntities";
import type { RewardsEarningRulesRewardEntity } from "@apps/rewards/modules/EarningRules/models/RewardsEarningRulesEntities";
import type { SpendingRulesEntity } from "@apps/rewards/modules/SpendingRules/entities/RewardsSpendingRulesEntities";
import type { VipTiersRewardEntity } from "@apps/rewards/modules/VipTiers/entities/RewardsVipTiersEntities";
import type { ProductsQuery } from "@modules/products/queries/ProductsQuery";
import type { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import type { ProductsState } from "@modules/products/stores/ProductsStore";
import type { Observable } from "rxjs";
import { combineLatest, filter, map, of, switchMap, take } from "rxjs";

export const fetchProduct = (
    reward:
        | (VipTiersRewardEntity | null)
        | RewardsEarningRulesRewardEntity
        | CustomerActiveRewardsEntity["reward"],
    productsService: ProductsServiceInterface
): void => {
    if (reward && reward.type === "free_product") {
        productsService.addProductForFetching(reward.productId);
    }
};

export const checkIsProductReady = (
    productId: number,
    productsQuery: ProductsQuery
): Observable<ProductsState["productStatuses"][number]["status"] | undefined> =>
    productsQuery
        .selectProductStatus(productId)
        .pipe(filter((status) => status === STATUS.READY));

//This three combine functions are temporary until all reward items will have the same product data structure
export const combineRewardProductData = <
    T extends {
        rewarding:
            | (VipTiersRewardEntity | null)
            | RewardsEarningRulesRewardEntity;
    }
>(
    items: T[],
    productsQuery: ProductsQuery
): Observable<T[]> => {
    if (!items.length) return of(items);

    return combineLatest(
        items.map((item) => {
            if (item.rewarding && item.rewarding.type === "free_product") {
                const { productId, variants } = item.rewarding;

                return checkIsProductReady(productId, productsQuery).pipe(
                    switchMap(() =>
                        productsQuery
                            .selectProductAndVariant(productId, variants[0].id)
                            .pipe(
                                map(({ product, variant }) => ({
                                    ...item,
                                    rewarding: {
                                        ...item.rewarding,
                                        productImage:
                                            product?.featuredImage?.url ?? null,
                                        productTitle: product?.title ?? "",
                                        productHandle: product?.handle ?? "",
                                        productId: product?.id ?? 0,
                                        variants: [
                                            {
                                                id: variant?.id ?? 0,
                                                title: variant?.title ?? "",
                                                image:
                                                    variant?.image?.url ?? null,
                                            },
                                        ],
                                    },
                                }))
                            )
                    )
                );
            }

            return of(item);
        })
    ).pipe(take(1));
};

export const combineCustomerRewardProductData = (
    items: CustomerActiveRewardsEntity[],
    productsQuery: ProductsQuery
): Observable<CustomerActiveRewardsEntity[]> => {
    if (!items.length) return of(items);

    return combineLatest(
        items.map((item) => {
            if (item.reward.type === "free_product") {
                const { productId, variantId } = item.reward;

                return checkIsProductReady(productId, productsQuery).pipe(
                    switchMap(() =>
                        productsQuery
                            .selectProductAndVariant(productId, variantId)
                            .pipe(
                                map(({ product, variant }) => ({
                                    ...item,
                                    reward: {
                                        ...item.reward,
                                        productImage:
                                            product?.featuredImage?.url ?? null,
                                        productTitle: product?.title ?? "",
                                        productHandle: product?.handle ?? "",
                                        productId: product?.id ?? 0,
                                        variantId: variant?.id ?? 0,
                                        variantTitle: variant?.title ?? "",
                                        variantImage:
                                            variant?.image?.url ?? null,
                                    },
                                }))
                            )
                    )
                );
            }

            return of(item);
        })
    ).pipe(take(1));
};

export const combineSpendingRulesProductData = (
    items: SpendingRulesEntity[],
    productsQuery: ProductsQuery
): Observable<SpendingRulesEntity[]> => {
    if (!items.length) return of(items);

    return combineLatest(
        items.map((item) => {
            if (
                item.metadata.products.length &&
                item.metadata.variants.length
            ) {
                const { products, variants } = item.metadata;

                return checkIsProductReady(products[0].id, productsQuery).pipe(
                    switchMap(() =>
                        productsQuery
                            .selectProductAndVariant(
                                products[0].id,
                                variants[0].id
                            )
                            .pipe(
                                map(({ product, variant }) => ({
                                    ...item,
                                    metadata: {
                                        icons: item.metadata.icons,
                                        products: [
                                            {
                                                id: product?.id ?? 0,
                                                title: product?.title ?? "",
                                                handle: product?.handle ?? "",
                                                image:
                                                    product?.featuredImage
                                                        ?.url ?? null,
                                            },
                                        ],
                                        variants: [
                                            {
                                                id: variant?.id ?? 0,
                                                title: variant?.title ?? "",
                                                price:
                                                    variant?.price.amount ?? 0,
                                                image:
                                                    variant?.image?.url ?? null,
                                            },
                                        ],
                                    },
                                }))
                            )
                    )
                );
            }

            return of(item);
        })
    ).pipe(take(1));
};
